import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  PDFViewer,
  Font,
} from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { Button, Spin } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

// Noto Sans SC fonts are preloaded in App.tsx via fontPreloader.ts
// Register fonts here (they should be cached by browser from preloading)
import { areFontsRegistered, textNeedsCjkFont } from "../utils/fontPreloader";

let notoSansSCRegistered = areFontsRegistered();

// If fonts weren't preloaded yet, register them now (will use browser cache if preloaded)
if (!notoSansSCRegistered) {
  try {
    Font.register({
      family: "NotoSansSC",
      fonts: [
        {
          src: "https://cdn.jsdelivr.net/npm/@fontsource/noto-sans-sc@5.2.9/files/noto-sans-sc-chinese-simplified-400-normal.woff",
          fontWeight: "normal",
        },
        {
          src: "https://cdn.jsdelivr.net/npm/@fontsource/noto-sans-sc@5.2.9/files/noto-sans-sc-chinese-simplified-700-normal.woff",
          fontWeight: "bold",
        },
      ],
    });
    notoSansSCRegistered = true;
  } catch (error) {
    console.warn(`⚠️ [PackingSlip] Font registration failed:`, error);
  }
}

const textColor = "#000000";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    padding: 20,
    display: "flex",
    flexDirection: "column",
    fontFamily: "Helvetica",
    color: textColor,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  referenceContainer: {
    alignItems: "flex-start",
  },
  referenceNumber: {
    fontSize: 24,
    fontWeight: "bold",
    color: textColor,
  },
  titleContainer: {
    alignItems: "flex-end",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: textColor,
  },
  infoRow: {
    marginTop: 5,
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  infoLabel: {
    fontSize: 10,
    marginRight: 5,
    color: textColor,
  },
  infoValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: textColor,
  },
  shipSection: {
    marginTop: 20,
    flexDirection: "row",
    marginBottom: 20,
    justifyContent: "space-between",
  },
  shipBox: {
    width: "48%",
    backgroundColor: "#f5f5f5",
    padding: 10,
  },
  shipBoxTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
    color: textColor,
  },
  shipBoxContent: {
    fontSize: 10,
    marginTop: 2,
    color: textColor,
  },
  supplierContent: {
    fontSize: 10,
    marginTop: 2,
    color: textColor,
  },
  table: {
    marginTop: 20,
    border: "1pt solid #000",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #000",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    flexDirection: "row",
    borderBottom: "1pt solid #000",
  },
  tableHeaderCell: {
    fontSize: 10,
    fontWeight: "bold",
    padding: 5,
    borderRight: "1pt solid #000",
    color: textColor,
  },
  tableCell: {
    fontSize: 9,
    padding: 5,
    borderRight: "1pt solid #000",
    color: textColor,
  },
  tableCellLast: {
    fontSize: 9,
    padding: 5,
    color: textColor,
  },
  tableHeaderCellLast: {
    fontSize: 10,
    fontWeight: "bold",
    padding: 5,
    color: textColor,
  },
  tableCellEAN: {
    width: "13%",
    color: textColor,
  },
  tableCellDescription: {
    width: "24%",
    textAlign: "left",
    color: textColor,
  },
  tableCellHsCode: {
    width: "18%",
    fontSize: 9,
    textAlign: "left",
    color: textColor,
  },
  tableCellPieces: {
    width: "7%",
    textAlign: "right",
    color: textColor,
  },
  tableCellCartons: {
    width: "7%",
    textAlign: "right",
    color: textColor,
  },
  tableCellWeight: {
    width: "10%",
    textAlign: "right",
    color: textColor,
  },
  tableCellNetWeight: {
    width: "10%",
    textAlign: "right",
    color: textColor,
  },
  tableCellCBM: {
    width: "11%",
    textAlign: "right",
    color: textColor,
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    borderBottom: "1pt solid #000",
  },
  errorContainer: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: textColor,
  },
  errorMessage: {
    fontSize: 10,
    textAlign: "center",
    color: textColor,
  },
  boldTotal: {
    fontWeight: "bold",
    fontSize: 11,
    letterSpacing: 0.2,
    color: textColor,
  },
  totalsTableRow: {
    flexDirection: "row",
    borderBottom: "2pt solid #000",
    borderTop: "2pt solid #000",
  },
  wrappableText: {
    maxWidth: "100%",
    whiteSpace: "pre-wrap",
    color: textColor,
  },
});

// Define types
export interface PackingSlipProduct {
  id: number;
  ean: string | number | null;
  description: string;
  pieces: number;
  cartons: number;
  weight: number; // in kg (gross weight)
  net_weight?: number; // optional net weight in kg
  cbm: number;
  hsCode?: string; // New optional field for HS Code
}

interface PackingSlipDocumentProps {
  date: string;
  reference: string;
  incoterm?: string;
  containerOrigin?: string;
  shipFrom?: {
    company: string;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  shipTo: {
    company: string;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    eori_number?: string;
    coc_number?: string;
    street?: string;
    house_number?: string;
  };
  supplierInfo?: {
    id?: number;
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    country?: string;
    province?: string;
  };
  products: PackingSlipProduct[];
}

// Create the PDF Document component
export const PackingSlipDocument = ({
  date,
  reference,
  incoterm,
  containerOrigin,
  shipTo,
  supplierInfo,
  products,
}: PackingSlipDocumentProps) => {
  const useCjkFont =
    notoSansSCRegistered &&
    textNeedsCjkFont([
      date,
      reference,
      incoterm,
      containerOrigin,
      shipTo.company,
      shipTo.address,
      shipTo.city,
      shipTo.postalCode,
      shipTo.country,
      shipTo.eori_number,
      shipTo.coc_number,
      shipTo.street,
      shipTo.house_number,
      supplierInfo?.name,
      supplierInfo?.email,
      supplierInfo?.phone,
      supplierInfo?.address,
      supplierInfo?.country,
      supplierInfo?.province,
      ...products.flatMap((p) => [p.description, p.ean, p.hsCode]),
    ]);
  const pageStyle = useCjkFont
    ? [styles.page, { fontFamily: "NotoSansSC" }]
    : styles.page;

  // Check if we have all required data
  const hasRequiredData =
    date && reference && shipTo && products && products.length > 0;

  // Calculate totals (memoized to avoid recalculation during render)
  const totalPieces = products.reduce((sum, p) => sum + (p.pieces || 0), 0);
  const totalCartons = products.reduce((sum, p) => sum + (p.cartons || 0), 0);
  const totalWeight = products.reduce((sum, p) => sum + (p.weight || 0), 0);
  const totalNetWeight = products.reduce(
    (sum, p) => sum + (p.net_weight || p.weight * 0.9),
    0,
  );
  const totalCBM = products.reduce((sum, p) => sum + (p.cbm || 0), 0);

  // Validate required data
  if (!hasRequiredData) {
    return (
      <Document>
        <Page size="A4" style={pageStyle}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorTitle}>Missing Required Data</Text>
            <Text style={styles.errorMessage}>
              Some required information is missing.
            </Text>
          </View>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        {/* Header */}
        <View style={styles.header}>
          {/* Reference Number at Top Left */}
          <View style={styles.referenceContainer}>
            <Text style={styles.referenceNumber}>{reference}</Text>
          </View>

          <View style={styles.titleContainer}>
            <Text style={styles.title}>PACKING LIST</Text>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Creation Date:</Text>
              <Text style={styles.infoValue}>{date}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Reference:</Text>
              <Text style={styles.infoValue}>{reference}</Text>
            </View>
            {incoterm && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Incoterm:</Text>
                <Text style={styles.infoValue}>{incoterm}</Text>
              </View>
            )}
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Container Origin:</Text>
              <Text style={styles.infoValue}>{containerOrigin || "-"}</Text>
            </View>
          </View>
        </View>

        {/* Customer and Supplier side by side */}
        <View style={styles.shipSection}>
          {/* Supplier section on the left */}
          {supplierInfo ? (
            <View style={styles.shipBox}>
              <Text style={styles.shipBoxTitle}>SUPPLIER</Text>
              {supplierInfo.name ? (
                <Text style={styles.supplierContent}>{supplierInfo.name}</Text>
              ) : null}
              {supplierInfo.address ? (
                <Text style={styles.supplierContent}>
                  {supplierInfo.address}
                </Text>
              ) : null}
              {supplierInfo.province && supplierInfo.country ? (
                <Text style={styles.supplierContent}>
                  {supplierInfo.province}, {supplierInfo.country}
                </Text>
              ) : null}
              {!supplierInfo.province && supplierInfo.country ? (
                <Text style={styles.supplierContent}>
                  {supplierInfo.country}
                </Text>
              ) : null}
              {supplierInfo.phone ? (
                <Text style={styles.supplierContent}>
                  Tel: {supplierInfo.phone}
                </Text>
              ) : null}
              {supplierInfo.email ? (
                <Text style={styles.supplierContent}>
                  Email: {supplierInfo.email}
                </Text>
              ) : null}
            </View>
          ) : (
            <View style={{ width: "48%" }} />
          )}

          {/* Customer section on the right */}
          <View style={styles.shipBox}>
            <Text style={styles.shipBoxTitle}>CUSTOMER</Text>
            <Text style={styles.shipBoxContent}>{shipTo.company || " "}</Text>
            {shipTo.street || shipTo.house_number ? (
              <Text style={styles.shipBoxContent}>
                {[shipTo.street, shipTo.house_number]
                  .filter(Boolean)
                  .join(" ") || " "}
              </Text>
            ) : null}
            {shipTo.address ? (
              <Text style={styles.shipBoxContent}>{shipTo.address}</Text>
            ) : null}
            {shipTo.postalCode || shipTo.city ? (
              <Text style={styles.shipBoxContent}>
                {[shipTo.postalCode, shipTo.city].filter(Boolean).join(", ") ||
                  " "}
              </Text>
            ) : null}
            {shipTo.country ? (
              <Text style={styles.shipBoxContent}>{shipTo.country}</Text>
            ) : null}
            {shipTo.eori_number ? (
              <Text style={styles.shipBoxContent}>
                EORI: {shipTo.eori_number}
              </Text>
            ) : null}
            {shipTo.coc_number ? (
              <Text style={styles.shipBoxContent}>
                COC: {shipTo.coc_number}
              </Text>
            ) : null}
          </View>
        </View>

        {/* Products Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.tableCellEAN]}>
              EAN
            </Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellDescription]}>
              Description
            </Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellHsCode]}>
              HS Code
            </Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellPieces]}>
              Pieces
            </Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellCartons]}>
              Cartons
            </Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellWeight]}>
              Gross Weight (kg)
            </Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellNetWeight]}>
              Net Weight (kg)
            </Text>
            <Text style={[styles.tableHeaderCellLast, styles.tableCellCBM]}>
              CBM
            </Text>
          </View>

          {/* Table Rows */}
          {products.map((product, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.tableCellEAN]}>
                {product.ean ? String(product.ean) : "N/A"}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellDescription,
                  styles.wrappableText,
                ]}
              >
                {product.description}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellHsCode]}>
                {product.hsCode ? product.hsCode.toString() : " "}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellPieces]}>
                {product.pieces}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellCartons]}>
                {product.cartons}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellWeight]}>
                {product.weight.toFixed(2)}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellNetWeight]}>
                {/* Calculate net weight as 90% of weight if not provided */}
                {product.net_weight
                  ? product.net_weight.toFixed(2)
                  : (product.weight * 0.9).toFixed(2)}
              </Text>
              <Text style={[styles.tableCellLast, styles.tableCellCBM]}>
                {product.cbm.toFixed(3)}
              </Text>
            </View>
          ))}

          {/* Totals Row */}
          <View style={styles.totalsTableRow}>
            <Text style={[styles.tableCell, styles.tableCellEAN]}> </Text>
            <Text
              style={[
                styles.tableCell,
                styles.tableCellDescription,
                styles.boldTotal,
              ]}
            >
              TOTALS
            </Text>
            <Text style={[styles.tableCell, styles.tableCellHsCode]}> </Text>
            <Text
              style={[
                styles.tableCell,
                styles.tableCellPieces,
                styles.boldTotal,
              ]}
            >
              {totalPieces}
            </Text>
            <Text
              style={[
                styles.tableCell,
                styles.tableCellCartons,
                styles.boldTotal,
              ]}
            >
              {totalCartons}
            </Text>
            <Text
              style={[
                styles.tableCell,
                styles.tableCellWeight,
                styles.boldTotal,
              ]}
            >
              {totalWeight.toFixed(2)}
            </Text>
            <Text
              style={[
                styles.tableCell,
                styles.tableCellNetWeight,
                styles.boldTotal,
              ]}
            >
              {totalNetWeight > 0 ? totalNetWeight.toFixed(2) : "-"}
            </Text>
            <Text
              style={[
                styles.tableCellLast,
                styles.tableCellCBM,
                styles.boldTotal,
              ]}
            >
              {totalCBM.toFixed(3)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// React component for displaying the packing slip
export const PackingSlip = ({
  date,
  reference,
  incoterm,
  containerOrigin,
  shipTo,
  supplierInfo,
  products,
}: PackingSlipDocumentProps) => {
  const [isGenerating, setIsGenerating] = useState(true);

  useEffect(() => {
    setIsGenerating(false);
  }, []);

  if (isGenerating) {
    return (
      <div style={{ textAlign: "center", padding: "10px" }}>
        <Spin size="small" />
        <p style={{ fontSize: "12px", margin: "5px 0 0" }}>
          Generating packing slip...
        </p>
      </div>
    );
  }

  // Create the document once
  const document = (
    <PackingSlipDocument
      date={date}
      reference={reference}
      incoterm={incoterm}
      containerOrigin={containerOrigin}
      shipTo={shipTo}
      supplierInfo={supplierInfo}
      products={products}
    />
  );

  return (
    <div style={{ textAlign: "center" }}>
      {/* Use PDFViewer for the preview */}
      <div
        style={{
          marginBottom: "15px",
          height: "500px",
          width: "100%",
          overflow: "hidden",
          border: "1px solid #ddd",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <PDFViewer
          width="100%"
          height="100%"
          showToolbar={false}
          style={{
            border: "none",
          }}
        >
          {document}
        </PDFViewer>
      </div>

      <PDFDownloadLink
        document={document}
        fileName={`packing-slip-${reference}.pdf`}
        style={{ textDecoration: "none" }}
      >
        {({ loading }) => (
          <Button
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            loading={loading}
          >
            Download Packing List
          </Button>
        )}
      </PDFDownloadLink>
    </div>
  );
};
